// frontend_app/src/lib/resultModel.js
//
// Turns raw result objects from the search stream into a uniform "model"
// the UI can rank, filter, compare and display. Sources label the same
// information differently ("Specimen Type", "Sample Type", "Tube/Container",
// "Min Test Volume"...), so key facts are found by matching field labels.

import { escapeRegExp, hashString, normalize, stripHtml } from './text';

// ── Sections ───────────────────────────────────────────────────────────────

export const SECTION_ORDER = [
  ['generalInfo', 'General information'],
  ['specimenCollection', 'Specimen & collection'],
  ['handlingAndProcessing', 'Handling & processing'],
  ['availability', 'Availability'],
  ['billingInfo', 'Billing'],
  ['labUseOnly', 'Laboratory use only'],
  ['geneticInfo', 'Genetic information'],
  ['panelInfo', 'Panel information'],
  ['relatedDocuments', 'Related documents'],
];

// Fields listed here appear first in their section, in this order; the rest
// follow alphabetically.
export const FIELD_PRIORITY_ORDER = {
  generalInfo: [
    'Test Name', 'Test Code', 'Test ID', 'Also Known As', 'Alternative Names',
    'Alternate Names', 'Synonyms', 'Laboratory', 'Department', 'Assay Frequency',
    'Turnaround Time', 'Description', 'Clinical Significance', 'Frequency',
  ],
  specimenCollection: [
    'Specimen Type', 'Specimen Types', 'Specimen types', 'Container', 'Container Type',
    'Container Types', 'Volume', 'Adults Volume or Mass',
    'Minimum/Paediatric Volume or Mass', 'Collection & Request Instructions',
    'Transport Instructions between Sites and/or Laboratories',
    'External Transport Instructions', 'Special Instructions', 'Stability',
  ],
  handlingAndProcessing: [
    'Storage', 'Temperature', 'Stability', 'Processing', 'Special Requirements', 'Instructions',
  ],
  labUseOnly: [
    'External Laboratory', 'Sending Lab Details', 'Notes', 'Comments',
    'Referred Laboratory', 'Reference Laboratory',
  ],
  billingInfo: ['Medicare Item Number', 'MBS Item', 'Schedule Fee', 'Benefit', 'Fee'],
  geneticInfo: ['Genes Tested', 'Gene', 'Panels Tested', 'Transcript', 'OMIM'],
};

/** "specimenCollection" → "Specimen Collection"; "test_code" → "Test code". */
export const formatKey = (key) => {
  if (!key) return '';
  return String(key)
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (c) => c.toUpperCase())
    .replace(/\s+/g, ' ')
    .trim();
};

const isPlainObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

export const sortSectionEntries = (sectionKey, data) => {
  if (!isPlainObject(data)) return data;
  const priority = new Map((FIELD_PRIORITY_ORDER[sectionKey] || []).map((k, i) => [k, i]));
  const entries = Object.entries(data);
  entries.sort(([a], [b]) => {
    const ai = priority.has(a) ? priority.get(a) : Infinity;
    const bi = priority.has(b) ? priority.get(b) : Infinity;
    if (ai !== bi) return ai - bi;
    return a.localeCompare(b);
  });
  return Object.fromEntries(entries);
};

const isEmptyValue = (v) =>
  v === null || v === undefined || v === '' ||
  (Array.isArray(v) && v.length === 0) ||
  (isPlainObject(v) && Object.keys(v).length === 0);

/** Ordered `[{ key, label, data }]` sections for the detail view. */
export const orderedSections = (details) => {
  if (!isPlainObject(details)) return [];
  const known = new Set(SECTION_ORDER.map(([k]) => k));
  const sections = [];
  SECTION_ORDER.forEach(([key, label]) => {
    if (!isEmptyValue(details[key])) sections.push({ key, label, data: sortSectionEntries(key, details[key]) });
  });
  const loose = {};
  Object.entries(details).forEach(([key, value]) => {
    if (key === 'Title' || known.has(key) || isEmptyValue(value)) return;
    if (isPlainObject(value)) sections.push({ key, label: formatKey(key), data: sortSectionEntries(key, value) });
    else loose[key] = value;
  });
  if (Object.keys(loose).length) sections.unshift({ key: '__details', label: 'Details', data: loose });
  return sections;
};

// ── Values ─────────────────────────────────────────────────────────────────

/** Plain-text rendering of any detail value. */
export const valueToText = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return stripHtml(value);
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return value.map(valueToText).filter(Boolean).join(', ');
  if (isPlainObject(value)) {
    if (value.text) return valueToText(value.text);
    if (value.label) return valueToText(value.label);
    return Object.values(value).map(valueToText).filter(Boolean).join(', ');
  }
  return '';
};

/** Every `{ section, key, value }` field in a details object. */
export const iterateFields = (details) => {
  const fields = [];
  if (!isPlainObject(details)) return fields;
  Object.entries(details).forEach(([sectionKey, sectionValue]) => {
    if (sectionKey === 'Title') return;
    if (isPlainObject(sectionValue)) {
      Object.entries(sectionValue).forEach(([key, value]) => fields.push({ section: sectionKey, key, value }));
    } else if (sectionKey !== 'relatedDocuments') {
      fields.push({ section: null, key: sectionKey, value: sectionValue });
    }
  });
  return fields;
};

// ── Key facts ──────────────────────────────────────────────────────────────

const normalizeLabel = (key) =>
  formatKey(key).toLowerCase().replace(/[^a-z0-9/&() ]+/g, ' ').replace(/\s+/g, ' ').trim();

// First matching rule wins, searched section by section (specimen sections first).
const FACT_RULES = {
  specimen: [/^specimen types?$/, /^sample types?$/, /^specimens?$/, /^specimen required$/, /^sample$/],
  container: [
    /^containers?$/, /^container types?$/, /^tube\/container$/, /^specimen containers?$/,
    /^tube colou?r$/, /^container colou?r$/, /^container \(min vol\)$/, /^tube( type)?$/,
    /collection information$/,
  ],
  volume: [
    /^min(imum)?( test| collection)? volume$/, /^adults? volume or mass$/, /^required volume adult/,
    /^preferred volume$/, /^sample volume$/, /^volume$/, /^spec type min$/,
    /^minimum\/paediatric volume or mass$/, /^req vol paediatric/,
  ],
  stability: [/stability/, /^storage( instructions| conditions)?$/],
  transport: [/^transport/, /transport$/, /^outside lab/],
  turnaround: [/turn ?around/, /\(tat\)/, /^tat$/, /^reporting time$/],
  performedAt: [/^performed( at)?$/, /^testing (laboratory|lab|facility)$/, /^laboratory$/, /^lab name$/],
  frequency: [/frequency/, /^days test is performed$/, /^when tested$/, /^day\/time$/, /^availability$/],
  testCode: [/^(pls )?test (code|id)$/, /^code$/, /^item code$/],
  referredTo: [/^external laboratory$/, /^sending lab details$/, /^referred (to|laboratory)$/, /^reference laboratory$/],
  aliases: [
    /^also known as$/, /^alternat(e|ive)( test)? names$/, /^synonyms$/, /^pseudonyms$/,
    /^other names/, /^alternative names$/,
  ],
};

// Facts that gather every matching field instead of the first one.
const COLLECT_ALL = new Set(['stability']);

const SECTION_SEARCH_ORDER = [
  'specimenCollection', 'handlingAndProcessing', 'generalInfo', 'availability', 'labUseOnly',
];

const sectionPriority = (section) => {
  const i = SECTION_SEARCH_ORDER.indexOf(section);
  return i === -1 ? SECTION_SEARCH_ORDER.length : i;
};

const NOT_APPLICABLE_RE = /^(n\/?a|none|nil|no|not applicable|-+|—|\.|see detailed information|unknown)$/i;

export const extractFacts = (details) => {
  const fields = iterateFields(details)
    .map((f, i) => ({ ...f, label: normalizeLabel(f.key), order: i }))
    .sort((a, b) => sectionPriority(a.section) - sectionPriority(b.section) || a.order - b.order);

  const facts = {};
  Object.entries(FACT_RULES).forEach(([fact, rules]) => {
    const matches = [];
    rules.forEach((rule) => {
      fields.forEach((field) => {
        if (!rule.test(field.label) || matches.some((m) => m.field === field)) return;
        const text = valueToText(field.value);
        if (text && !NOT_APPLICABLE_RE.test(text)) matches.push({ field, text });
      });
    });
    if (!matches.length) return;
    if (COLLECT_ALL.has(fact) && matches.length > 1) {
      facts[fact] = matches.map((m) => `${formatKey(m.field.key)}: ${m.text}`).join('; ');
    } else {
      facts[fact] = matches[0].text;
    }
  });
  return facts;
};

// ── Specimen categories ────────────────────────────────────────────────────

const SPECIMEN_CATEGORIES = [
  ['Blood', /\b(blood|serum|plasma|sst|edta|heparin|citrate|fluoride|clott?ed|gold top|red top|lavender|gel tube|whole blood|acd|trace element)\b/],
  ['Urine', /\b(urine|msu|csu)\b/],
  ['CSF', /\b(csf|cerebrospinal)\b/],
  ['Swab', /\bswabs?\b/],
  ['Faeces', /\b(faec\w*|fec(al|es)|stool)\b/],
  ['Tissue', /\b(tissue|biopsy|bone marrow|aspirate|fna)\b/],
  ['Other', /\b(fluid|saliva|sputum|semen|sweat|breath|hair|nails?|skin|scrapings?|tears?|milk|calculus|stone)\b/],
];

export const SPECIMEN_FACET_ORDER = ['Blood', 'Urine', 'CSF', 'Swab', 'Faeces', 'Tissue', 'Other', 'Not stated'];

export const specimenCategories = (facts) => {
  const text = `${facts.specimen || ''} ${facts.container || ''}`.toLowerCase();
  if (!text.trim()) return [];
  const categories = SPECIMEN_CATEGORIES.filter(([, re]) => re.test(text)).map(([name]) => name);
  return categories.length ? categories : ['Other'];
};

// ── Titles & ranking ───────────────────────────────────────────────────────

const TRAILING_SPECIMEN_RE = /\s+(serum|plasma|whole blood|blood|urine|csf|edta|sst)$/;

/** Title without parentheticals or a trailing specimen word: "FERRITIN,SERUM" → "ferritin". */
export const coreTitle = (title) => {
  let core = normalize(String(title || '').replace(/\([^)]*\)/g, ' '));
  let previous;
  do {
    previous = core;
    core = core.replace(TRAILING_SPECIMEN_RE, '').trim();
  } while (core !== previous && core);
  return core || normalize(title);
};

export const RANK_LABELS = [
  'Exact title', 'Exact synonym', 'Title starts with', 'Title contains word', 'Synonym contains', 'Title contains', 'Other field',
];

/** 0 (best) … 6: how closely the result matches the query. */
export const rankResult = (model, query) => {
  const q = normalize(query);
  if (!q) return 6;
  const title = model.normTitle;
  const core = model.coreTitle;
  if (title === q || core === q) return 0;
  if (model.normAliases.includes(q)) return 1;
  if (title.startsWith(`${q} `) || core.startsWith(q)) return 2;
  if (new RegExp(`(^|\\s)${escapeRegExp(q)}`).test(title)) return 3;
  if (model.normAliases.some((a) => a.includes(q))) return 4;
  if (title.includes(q)) return 5;
  return 6;
};

export const compareModels = (a, b) =>
  a.rank - b.rank ||
  a.coreTitle.length - b.coreTitle.length ||
  a.labName.localeCompare(b.labName) ||
  a.titleText.localeCompare(b.titleText);

// ── Model ──────────────────────────────────────────────────────────────────

const splitAliases = (text) =>
  text
    .split(/[,;\n|]|\s\/\s/)
    .map((s) => s.trim())
    .filter((s) => s.length > 1 && s.length <= 60);

const safeLink = (link) => (typeof link === 'string' && /^https?:\/\//i.test(link.trim()) ? link.trim() : null);

/**
 * Build the UI model for one raw result.
 * `pkg` is its stream package ({ source_name, display_group }); `source` is the
 * matching entry from /sources (may be undefined for custom databases).
 * `groupSize` is how many sources share the display group (for sub-lab labels).
 */
export const buildResultModel = ({ raw, pkg, source, groupSize = 1, query = '', idSuffix = '' }) => {
  const details = raw.details || raw.inline_details || {};
  // The package's source_name is the configured source name; some parsers put
  // a display label in raw.source instead (e.g. "Austin Pathology" for "Austin").
  const sourceName = pkg.source_name || raw.source || '';
  const labGroup = raw.display_group || pkg.display_group || sourceName;
  const titleText = stripHtml(details.Title || raw.title || 'Untitled test');
  const link = safeLink(raw.link);
  const facts = extractFacts(details);

  const aliasText = [facts.aliases, raw.other_keywords].filter(Boolean).join(', ');
  const aliases = Array.from(new Set(splitAliases(aliasText)))
    .filter((a) => normalize(a) !== normalize(titleText));

  const isCustom = sourceName === 'Local File Search';
  // Brand within a multi-source lab, e.g. "DHM" for Sonic Healthcare.
  const subLab = !isCustom && groupSize > 1
    ? sourceName.replace(/\s*\(Sonic\)\s*/i, '').replace(/^PanelApp\s+/i, '')
    : '';

  const labName = isCustom ? labGroup.replace(/\s*-\s*Custom Databases$/, '') : labGroup;
  const referred = Boolean(facts.referredTo);

  const model = {
    id: hashString(`${sourceName}|${link || ''}|${titleText}`) + idSuffix,
    raw,
    details,
    sourceName,
    labGroup,
    labName,
    subLab,
    isCustom,
    region: isCustom ? 'Custom' : (source && source.region) || '',
    homepage: (source && source.homepage) || '',
    titleText,
    link,
    facts,
    aliases,
    specimenCategories: specimenCategories(facts),
    referral: referred ? 'referred' : 'in-house',
    hasInlineDetails: Object.keys(details).length > 0,
    canFetchDetails: Boolean(source && source.has_details_endpoint && link),
    normTitle: normalize(titleText),
    coreTitle: coreTitle(titleText),
    normAliases: aliases.map(normalize),
  };
  model.rank = rankResult(model, query);
  return model;
};

/**
 * Models for the displayable results of one stream package. `seen` is shared
 * across the packages of a search so duplicate ids get a numeric suffix.
 */
export const buildPackageModels = (pkg, { sourcesByName = {}, groupSizes = {}, query = '', seen = new Map() } = {}) => {
  const models = [];
  (pkg.results || []).forEach((raw) => {
    if (!raw || raw.is_displayable === false) return;
    const sourceName = pkg.source_name || raw.source;
    const labGroup = raw.display_group || pkg.display_group;
    const model = buildResultModel({
      raw, pkg, source: sourcesByName[sourceName], groupSize: groupSizes[labGroup] || 1, query,
    });
    const count = seen.get(model.id) || 0;
    seen.set(model.id, count + 1);
    if (count > 0) model.id = `${model.id}-${count}`;
    models.push(model);
  });
  return models;
};

/** Models for every displayable result in `packages` (ids unique across them). */
export const buildModels = (packages, options = {}) => {
  const seen = new Map();
  return packages.flatMap((pkg) => buildPackageModels(pkg, { ...options, seen }));
};

// ── Text used by the Refine filter ─────────────────────────────────────────

const collectText = (value, out) => {
  if (value === null || value === undefined) return;
  if (typeof value === 'string') out.push(stripHtml(value));
  else if (typeof value === 'number') out.push(String(value));
  else if (Array.isArray(value)) value.forEach((v) => collectText(v, out));
  else if (typeof value === 'object') Object.values(value).forEach((v) => collectText(v, out));
};

const searchTextCache = new WeakMap();

/** Lower-cased text of every *value* in the result (never field names). */
export const searchTextOf = (model) => {
  let text = searchTextCache.get(model);
  if (text === undefined) {
    const parts = [model.titleText, model.labName, model.subLab, model.aliases.join(' ')];
    collectText(model.details, parts);
    text = parts.join(' \u0001 ').toLowerCase().replace(/\s+/g, ' ');
    searchTextCache.set(model, text);
  }
  return text;
};
